import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { HelpCircle, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import {
  getAll,
  create,
  update,
  remove,
} from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_FAQS } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "faqs";
const EMPTY_FORM = { question: "", answer: "" };

export default function FaqsEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAll(COLLECTION);
      setItems(data);
    } catch {
      toast.error("Gagal memuat FAQ");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      question: item.question || "",
      answer: item.answer || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.question.trim()) {
      toast.error("Pertanyaan wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await update(COLLECTION, editing.id, form);
        toast.success("FAQ berhasil diperbarui!");
      } else {
        await create(COLLECTION, form);
        toast.success("FAQ berhasil dibuat!");
      }
      await updateCollectionVersion(COLLECTION);
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(COLLECTION, deleteTarget.id);
      toast.success("FAQ berhasil dihapus!");
      await updateCollectionVersion(COLLECTION);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
            <h1 className="text-2xl font-display text-[#fdfbf7]">FAQ</h1>
          </div>
          <p className="text-sm text-[#a6a196] ml-4">
            Kelola pertanyaan yang sering diajukan
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SeedDataButton
            collectionName={COLLECTION}
            data={SEED_FAQS}
            onComplete={loadItems}
            isEmpty={items.length === 0}
          />
          <Button
            onClick={openCreate}
            className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#a6a196] border border-dashed border-[#d4af37]/20 rounded-lg">
          <HelpCircle className="w-12 h-12 mb-3 opacity-20" />
          <p className="mb-4 text-sm">Belum ada FAQ</p>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-[#d4af37]/30 text-[#d4af37]"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah FAQ
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Table view */}
          <div className="rounded-lg border border-[#d4af37]/10 overflow-hidden bg-[#0a0908]">
            <div className="px-4 py-3 border-b border-[#d4af37]/10">
              <p className="text-xs text-[#a6a196] uppercase tracking-wider font-medium">
                Semua FAQ ({items.length})
              </p>
            </div>
            <div className="divide-y divide-[#d4af37]/5">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="px-4 py-3 hover:bg-[#15120f]/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#fdfbf7] font-medium truncate">
                        {item.question}
                      </p>
                      <p className="text-xs text-[#a6a196] mt-1 line-clamp-2">
                        {item.answer}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(item)}
                        className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteTarget(item)}
                        className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Accordion preview */}
          <div className="rounded-lg border border-[#d4af37]/10 bg-[#0a0908] p-4">
            <p className="text-xs text-[#a6a196] uppercase tracking-wider font-medium mb-3">
              Pratinjau
            </p>
            <Accordion type="single" collapsible className="w-full">
              {items.map((item) => (
                <AccordionItem
                  key={item.id}
                  value={item.id}
                  className="border-[#d4af37]/10"
                >
                  <AccordionTrigger className="text-sm text-[#fdfbf7] hover:text-[#d4af37] py-3 text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#a6a196] pb-3">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#fdfbf7]">
              {editing ? "Edit FAQ" : "Tambah FAQ"}
            </DialogTitle>
            <DialogDescription className="text-[#a6a196]">
              {editing
                ? "Ubah pertanyaan dan jawaban"
                : "Isi pertanyaan dan jawaban baru"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Pertanyaan</label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Pertanyaan yang sering diajukan"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Jawaban</label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                placeholder="Jawaban lengkap"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50 min-h-[120px]"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#d4af37]/20 text-[#a6a196]"
            >
              Batal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Menyimpan...
                </>
              ) : editing ? (
                "Perbarui"
              ) : (
                "Buat"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#fdfbf7]">
              Hapus FAQ
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a6a196]">
              Apakah Anda yakin ingin menghapus FAQ ini? Tindakan ini tidak
              dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0a0908] border-[#d4af37]/20 text-[#a6a196] hover:bg-[#15120f]">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="text-red-400 border bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
